import { makeid } from 'utils/helpers/makeid';
import { DateFormatter } from 'infrastructure/functions';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createPulseOxOnboardingTemplate = (data: ITemplateProps) => {
  const { userFullname } = data;

  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. I'm calling to help
          you get started with your Remote Patient Monitoring program. For
          quality and compliance purposes, this call may be recorded. To protect
          your privacy, can you please confirm your full name and date of
          birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient's caregiver? Do I have the patient's
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'onboardingPurposeReassurance',
      type: 'checkbox',
      title: <p>Onboarding Purpose & Reassurance</p>,
      description: (
        <ScriptWrapper>
          "The goal of today's call is to help you get your pulse oximeter set
          up, make sure everything is working, and take your first reading if
          we're able."
          <br />
          "There's no way to do this wrong — I'll guide you step by step, and if
          something doesn't work right away, that's completely okay."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Purpose explained',
          value: 'purposeExplained',
        },
        {
          key: makeid(10),
          label: 'Reassurance provided',
          value: 'reassuranceProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'earlyGoalSetting',
      type: 'checkbox',
      title: <p>Early Goal Setting (Clinical Best Practice)</p>,
      description: (
        <ScriptWrapper>
          "As part of your care, your clinic recommends checking your oxygen
          level and heart rate as often as possible, ideally daily."
          <br />
          "This helps your care team understand your normal range and notice
          changes early."
          <br />
          "If you miss a day, that's okay — consistency over time is what
          matters most."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Daily readings encouraged as best practice',
          value: 'dailyReadingsEncouragedAsBestPractice',
        },
        {
          key: makeid(10),
          label: 'Flexibility and consistency emphasized',
          value: 'flexibilityAndConsistencyEmphasized',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceReadiness',
      type: 'radio',
      title: <p>Device Readiness Check</p>,
      description: (
        <ScriptWrapper>
          "Do you have the pulse oximeter with you right now?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Device present during call',
          value: 'devicePresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — let's get everything ready together."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Device not present during call',
          value: 'deviceNotPresentDuringCall',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — we can review how to use it today, and you can
                  take your first reading later."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'deviceSetupBatteryReview',
      type: 'checkbox',
      title: <p>Device Setup & Battery Review</p>,
      description: (
        <ScriptWrapper>
          "Inside the box, you should have the pulse oximeter and two AA
          batteries."
          <br />
          "Please let me know once the batteries are in and the device turns
          on."
          <br />
          If patient unsure: "That's okay — take your time. Let me know what you
          see on the screen."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Batteries inserted successfully',
          value: 'batteriesInsertedSuccessfully',
        },
        {
          key: makeid(10),
          label: 'Power confirmed',
          value: 'powerConfirmed',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'fingerPlacementPositioningReviewed',
      type: 'checkbox',
      title: <p>Finger Placement & Positioning Reviewed</p>,
      description: (
        <ScriptWrapper>
          "Let's make sure the reading is as accurate as possible."
          <br />
          "Place the device on your fingertip so your finger rests fully
          inside."
          <br />
          "Make sure your hand is warm and relaxed."
          <br />
          "Rest your hand on a table or your lap."
          <br />
          "Try to sit still and avoid talking while the reading is running."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Proper finger placement',
          value: 'properFingerPlacement',
        },
        {
          key: makeid(10),
          label: 'Hand supported and relaxed',
          value: 'handSupportedAndRelaxed',
        },
        {
          key: makeid(10),
          label: 'Patient remained still during reading',
          value: 'patientRemainedStillDuringReading',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'firstReadingCompletion',
      type: 'radio',
      title: <p>First Reading Completion</p>,
      description: (
        <ScriptWrapper>
          "Once your finger is in place, the device will begin the reading
          automatically."
          <br />
          "You'll see numbers on the screen as it measures."
          <br />
          (Allow time.)
          <br />
          "When the reading is complete, the device will display the word
          'Success.'"
          <br />
          "After a few seconds, the device will turn off on its own — that's
          normal."
          <br />
          "That power-off is how the reading is sent to your care team."
          <br />
          "Please tell me when you see 'Success' on the screen."
          <br />
          If successful: "Perfect — you did it. That reading went through."
          <br />
          If unsuccessful: "That's okay — sometimes it takes a second try. We
          can try again or follow up later."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Reading completed successfully',
          value: 'readingCompletedSuccessfully',
        },
        {
          key: makeid(10),
          label: 'Reading attempted but unsuccessful',
          value: 'readingAttemptedButUnsuccessful',
        },
        {
          key: makeid(10),
          label: 'Reading deferred',
          value: 'readingDeferred',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'readingTransmissionConfirmed',
      type: 'checkbox',
      title: <p>Reading Transmission Confirmed</p>,
      description: '',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: '"Success" displayed',
          value: 'successDisplayed',
        },
        {
          key: makeid(10),
          label: 'Device powered off automatically',
          value: 'devicePoweredOffAutomatically',
        },
        {
          key: makeid(10),
          label: 'Reading transmitted',
          value: 'readingTransmitted',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'programValueReinforced',
      type: 'checkbox',
      title: <p>Program Value Reinforced</p>,
      description: (
        <ScriptWrapper>
          "Your oxygen level and heart rate readings are securely shared with
          your care team."
          <br />
          "Even normal readings are important — they help establish your
          baseline and catch changes early."
          <br />
          "If readings are missing for an extended period, you may receive
          reminder texts or a call from us — just to make sure everything's okay
          and see if you need help."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Trend monitoring explained',
          value: 'trendMonitoringExplained',
        },
        {
          key: makeid(10),
          label: 'Engagement encouraged',
          value: 'engagementEncouraged',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'insuranceVerificationOnFile',
      type: 'radio',
      title: <p>Insurance Verification (On File Only)</p>,
      description: (
        <ScriptWrapper>
          "I want to confirm the type of insurance we have on file for you."
          <br />
          "What type of insurance do you have listed with your clinic: Medicare,
          Medicare Advantage, or Commercial?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Medicare',
          value: 'medicare',
        },
        {
          key: makeid(10),
          label: 'Medicare Advantage (plan selected)',
          value: 'medicareAdvantage',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Which plan is listed?"
                  <br />
                  "Thank you — I'll confirm what's documented in your chart."
                </ScriptWrapper>
              ),
            },
            {
              key: makeid(10),
              type: 'checkbox',
              name: 'insuranceTypeOnFile',
              options: [
                {
                  key: makeid(10),
                  label: 'Insurance type on file confirmed',
                  value: 'insuranceTypeOnFileConfirmed',
                },
              ],
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Commercial',
          value: 'commercial',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'cellPhoneVerification',
      type: 'radio',
      title: <p>Cell Phone Verification</p>,
      description: (
        <ScriptWrapper>
          "Before we continue, I want to confirm the best cell phone number to
          reach you. Is [number on file] still correct?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Cell phone number confirmed',
          value: 'cellPhoneNumberConfirmed',
        },
        {
          key: makeid(10),
          label: 'Cell phone number updated',
          value: 'cellPhoneNumberUpdated',
        },
        {
          key: makeid(10),
          label: 'Patient reports no cell phone',
          value: 'patientReportsNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'textingEnrollment',
      type: 'radio',
      title: <p>Texting Enrollment</p>,
      description: (
        <ScriptWrapper>
          "If you have a cell phone, we also offer text messages for reminders
          and updates related to your RPM care."
          <br />
          "Would you like to receive text messages from us?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Already enrolled',
          value: 'alreadyEnrolled',
        },
        {
          key: makeid(10),
          label: 'Offered and accepted → Enrolled',
          value: 'offeredAndAcceptedEnrolled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Great — I'll get you enrolled. You can opt out at any time."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Offered and declined',
          value: 'offeredAndDeclined',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "No problem — that's completely optional."
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Not eligible — no cell phone',
          value: 'notEligibleNoCellPhone',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionRequirement',
      type: 'checkbox',
      title: <p>Monthly Interaction Requirement + Scheduling</p>,
      description: (
        <ScriptWrapper>
          "As part of the program, a monthly phone call with our team is
          required."
          <br />
          "These calls help us check in, answer questions, and make sure
          everything continues to work smoothly."
          <br />
          "When your readings stay within your doctor's recommended range,
          they're reviewed as trends over time."
          <br />
          "If a reading falls outside of that range, your clinic may reach out —
          often with a call from a nurse — to check in and decide if follow-up
          is needed."
          <br />
          "Would you like to schedule your first monthly check-in now, or should
          we reach out closer to when it's due?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Monthly phone call requirement reviewed',
          value: 'monthlyPhoneCallRequirementReviewed',
        },
        {
          key: makeid(10),
          label: 'Program monitoring process explained',
          value: 'programMonitoringProcessExplained',
        },
        {
          key: makeid(10),
          label: 'Nurse or clinic outreach for out-of-range readings explained',
          value: 'nurseOrClinicOutreachForOutOfRangeReadingsExplained',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'monthlyInteractionScheduling',
      title: <p>Monthly Interaction Scheduling</p>,
      type: 'radio',
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'First monthly call scheduled (date)',
          value: 'nextMonthlyCheckInScheduled',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'input-date',
              required: false,
              name: 'nextMonthlyCheckInScheduledDate',
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Patient declined scheduling',
          value: 'patientDeclinedScheduling',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingAdditionalQuestions',
      type: 'radio',
      title: <p>Closing - Additional Questions</p>,
      description: (
        <ScriptWrapper>
          "Before we disconnect, do you have any additional questions for me
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Had no additional questions',
          value: 'noAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Great — thank you."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Asked questions — addressed during call',
          value: 'hasAdditionalQuestions',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>"Does that answer your question?"</ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'closingUnderstanding',
      type: 'radio',
      title: <p>Closing - Understanding</p>,
      description: (
        <ScriptWrapper>
          "Just to confirm, do you feel you understand everything we discussed
          today?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Verbalized full understanding',
          value: 'full',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: <ScriptWrapper>"Perfect."</ScriptWrapper>,
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Verbalized partial understanding',
          value: 'partial',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you for letting me know. Is there anything I can
                  clarify before we finish?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Unable to verbalize understanding',
          value: 'unable',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Thank you — I'll document that and we can follow up as
                  needed."
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Final close</p>,
      description: (
        <ScriptWrapper>
          "Thank you for your time today. Please feel free to reach out if you
          need additional support. Take care."
        </ScriptWrapper>
      ),
    },
  ] as const satisfies TFormTemplateItem[];
};

export type TPulseOxOnboardingTemplateValues = ExtractStepValues<
  ReturnType<typeof createPulseOxOnboardingTemplate>
>;

export const createPulseOxOnboardingBlurb = (
  data: TPulseOxOnboardingTemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    onboardingPurposeReassurance,
    earlyGoalSetting,
    deviceReadiness,
    deviceSetupBatteryReview,
    fingerPlacementPositioningReviewed,
    firstReadingCompletion,
    readingTransmissionConfirmed,
    programValueReinforced,
    insuranceVerificationOnFile,
    cellPhoneVerification,
    textingEnrollment,
    monthlyInteractionRequirement,
    monthlyInteractionScheduling,
    nextMonthlyCheckInScheduledDate,
    closingAdditionalQuestions,
    closingUnderstanding,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');
    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    onboardingPurposeReassurance?.length &&
    !hasField(ctx, 'onboardingPurposeReassurance')
  ) {
    addField(ctx, 'onboardingPurposeReassurance');
    const map = {
      purposeExplained: 'Onboarding call purpose explained.',
      reassuranceProvided:
        'Patient reassured regarding pulse oximeter setup and available support.',
    };
    txt.push(...mapValuesToText(onboardingPurposeReassurance, map));
  }

  if (earlyGoalSetting?.length && !hasField(ctx, 'earlyGoalSetting')) {
    addField(ctx, 'earlyGoalSetting');
    const map = {
      dailyReadingsEncouragedAsBestPractice:
        'Clinical best practice for frequent oxygen saturation and heart rate readings discussed.',
      flexibilityAndConsistencyEmphasized:
        'Importance of consistency over time emphasized.',
    };
    txt.push(...mapValuesToText(earlyGoalSetting, map));
  }

  if (deviceReadiness && !hasField(ctx, 'deviceReadiness')) {
    addField(ctx, 'deviceReadiness');
    const map = {
      devicePresentDuringCall: 'present during call',
      deviceNotPresentDuringCall: 'not present during call',
    };

    txt.push(
      `Pulse oximeter availability reviewed. Device ${map[deviceReadiness]}.`,
    );

    if (deviceReadiness === 'deviceNotPresentDuringCall') {
      txt.push('Education provided and first reading deferred.');
    }
  }

  if (
    deviceSetupBatteryReview?.length &&
    !hasField(ctx, 'deviceSetupBatteryReview')
  ) {
    addField(ctx, 'deviceSetupBatteryReview');
    const map = {
      batteriesInsertedSuccessfully:
        'Pulse oximeter setup reviewed. Batteries inserted successfully.',
      powerConfirmed: 'Device powered on and confirmed functioning.',
    };
    txt.push(...mapValuesToText(deviceSetupBatteryReview, map));
  }

  if (
    fingerPlacementPositioningReviewed?.length &&
    !hasField(ctx, 'fingerPlacementPositioningReviewed')
  ) {
    addField(ctx, 'fingerPlacementPositioningReviewed');
    const map = {
      properFingerPlacement: 'Proper finger placement reviewed and confirmed.',
      handSupportedAndRelaxed: 'Hand supported and relaxed during reading.',
      patientRemainedStillDuringReading:
        'Patient remained still during reading.',
    };
    txt.push(...mapValuesToText(fingerPlacementPositioningReviewed, map));
  }

  if (firstReadingCompletion && !hasField(ctx, 'firstReadingCompletion')) {
    addField(ctx, 'firstReadingCompletion');
    const map = {
      readingCompletedSuccessfully: 'completed successfully',
      readingAttemptedButUnsuccessful: 'attempted but unsuccessful',
      readingDeferred: 'deferred',
    };
    txt.push(`First pulse oximeter reading ${map[firstReadingCompletion]}.`);
  }

  if (
    readingTransmissionConfirmed?.length &&
    !hasField(ctx, 'readingTransmissionConfirmed')
  ) {
    addField(ctx, 'readingTransmissionConfirmed');
    const map = {
      successDisplayed: 'Reading completed with “Success” displayed.',
      devicePoweredOffAutomatically:
        'Device powered off automatically after the reading.',
      readingTransmitted: 'Reading transmission confirmed successfully.',
    };
    txt.push(...mapValuesToText(readingTransmissionConfirmed, map));
  }

  if (
    programValueReinforced?.length &&
    !hasField(ctx, 'programValueReinforced')
  ) {
    addField(ctx, 'programValueReinforced');
    const map = {
      trendMonitoringExplained:
        'Importance of baseline and trend monitoring reviewed.',
      engagementEncouraged:
        'Patient engagement in ongoing monitoring encouraged.',
    };
    txt.push(...mapValuesToText(programValueReinforced, map));
  }

  if (
    insuranceVerificationOnFile &&
    !hasField(ctx, 'insuranceVerificationOnFile')
  ) {
    addField(ctx, 'insuranceVerificationOnFile');
    const map = {
      medicare: 'Medicare',
      medicareAdvantage: 'Medicare Advantage (plan selected)',
      commercial: 'Commercial',
    };
    txt.push(
      `Insurance type confirmed as ${map[insuranceVerificationOnFile]} based on information on file and updated in patient chart as needed.`,
    );
  }

  if (cellPhoneVerification && !hasField(ctx, 'cellPhoneVerification')) {
    addField(ctx, 'cellPhoneVerification');
    const map = {
      cellPhoneNumberConfirmed: 'confirmed',
      cellPhoneNumberUpdated: 'updated',
      patientReportsNoCellPhone: 'patient reports no cell phone',
    };
    txt.push(
      `Cell phone contact verified with patient ${map[cellPhoneVerification]}.`,
    );
  }

  if (textingEnrollment && !hasField(ctx, 'textingEnrollment')) {
    addField(ctx, 'textingEnrollment');
    const map = {
      alreadyEnrolled: 'already enrolled',
      offeredAndAcceptedEnrolled: 'offered and accepted',
      offeredAndDeclined: 'offered and declined',
      notEligibleNoCellPhone: 'not eligible—no cell phone',
    };
    txt.push(`Texting enrollment reviewed ${map[textingEnrollment]}.`);
  }

  if (
    monthlyInteractionRequirement?.length &&
    !hasField(ctx, 'monthlyInteractionRequirement')
  ) {
    addField(ctx, 'monthlyInteractionRequirement');
    const map = {
      monthlyPhoneCallRequirementReviewed: 'monthly phone call requirement',
      programMonitoringProcessExplained: 'program monitoring process explained',
      nurseOrClinicOutreachForOutOfRangeReadingsExplained:
        'that out-of-range readings may result in nurse or clinic outreach',
    };
    const items = mapValuesToText(monthlyInteractionRequirement, map);
    if (items.length) {
      txt.push(
        `Monthly interaction requirement reviewed with patient, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    monthlyInteractionScheduling &&
    !hasField(ctx, 'monthlyInteractionScheduling')
  ) {
    addField(ctx, 'monthlyInteractionScheduling');
    const map = {
      nextMonthlyCheckInScheduled: 'scheduled',
      patientDeclinedScheduling: 'not scheduled—patient declined',
    };
    let val = map[monthlyInteractionScheduling];
    if (
      monthlyInteractionScheduling === 'nextMonthlyCheckInScheduled' &&
      nextMonthlyCheckInScheduledDate
    ) {
      val += `in ${DateFormatter({
        date: nextMonthlyCheckInScheduledDate,
      })}`;
    }

    txt.push(`First monthly check-in ${val}.`);
  }

  if (
    closingAdditionalQuestions &&
    !hasField(ctx, 'closingAdditionalQuestions')
  ) {
    addField(ctx, 'closingAdditionalQuestions');

    const map = {
      noAdditionalQuestions: 'had no additional questions',
      hasAdditionalQuestions:
        'asked questions which were addressed during the call',
    };

    txt.push(
      `Call concluded after confirming patient ${map[closingAdditionalQuestions]}.`,
    );
  }

  if (closingUnderstanding && !hasField(ctx, 'closingUnderstanding')) {
    addField(ctx, 'closingUnderstanding');

    const map = {
      full: 'verbalized full understanding',
      partial: 'verbalized partial understanding',
      unable: 'was unable to verbalize understanding',
    };

    txt.push(
      `Patient ${map[closingUnderstanding]} of the information discussed.`,
    );
  }

  return txt.join(' ');
};
